import React, { useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import DirectionsCarFilledOutlined from "@mui/icons-material/DirectionsCarFilledOutlined";
import CloseRounded from "@mui/icons-material/CloseRounded";
import SearchRounded from "@mui/icons-material/SearchRounded";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import ErrorRounded from "@mui/icons-material/ErrorRounded";
import ReceiptLongOutlined from "@mui/icons-material/ReceiptLongOutlined";
import styled from "styled-components";
import { useGlobalContext } from "../../context/GlobalContext";
import useGet from "../../hooks/common/useGet";
import { API_CHECK_VEHICLE_STATUS } from "../../utils/apis";
import { formatAmount } from "../../utils/helpers";
import { useNavigate } from "react-router-dom";

const tone = (status?: string) => {
  if (status === "NIL") return "clear";
  if (status === "Defaulted") return "danger";
  return "warn";
};

export const VehicleStatusModal: React.FC = () => {
  const navigate = useNavigate();
  const [plateNumber, setPlateNumber] = useState<string>("");

  const { data: globalData, updateData } = useGlobalContext();
  const { openVehicleStatus } = globalData || {};

  const {
    data: vehicleStatusData,
    loadData,
    loading,
    error: statusError,
    success,
    reset,
  } = useGet();

  const result: any = success ? vehicleStatusData : null;
  const status: string | undefined = result?.status;
  const isClear = status === "NIL";
  const isDefaulted = status === "Defaulted";

  const handleClose = (): void => {
    updateData("openVehicleStatus", false);
    setPlateNumber("");
    reset();
  };

  const handleCheckStatus = (e: React.FormEvent): void => {
    e.preventDefault();
    reset();
    loadData({
      api: API_CHECK_VEHICLE_STATUS,
      params: { number_plate: plateNumber },
    });
  };

  // a result belongs to the plate it was checked for, so editing clears it
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setPlateNumber(event.target.value);
    if (success || statusError) reset();
  };

  const goToClamping = (actionType: "notified" | "clamped") => {
    navigate(
      `/clamping/create?action_type=${actionType}&car_number=${encodeURIComponent(plateNumber)}`,
    );
    handleClose();
  };

  return (
    <Dialog
      open={!!openVehicleStatus}
      onClose={handleClose}
      aria-labelledby="vehicle-status-dialog-title"
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: "var(--lp-r-lg)",
          boxShadow: "var(--lp-shadow-lg)",
          overflow: "hidden",
        },
      }}
    >
      <Header>
        <span className="icon-tile">
          <DirectionsCarFilledOutlined fontSize="small" />
        </span>
        <div className="heading">
          <h2 id="vehicle-status-dialog-title">Check Vehicle Status</h2>
          <p>Look up a plate for unpaid parking invoices.</p>
        </div>
        <IconButton aria-label="Close" size="small" onClick={handleClose}>
          <CloseRounded fontSize="small" />
        </IconButton>
      </Header>

      <Body>
        <form className="search" onSubmit={handleCheckStatus}>
          <label htmlFor="plate_number">Plate number</label>
          <div className="search-row">
            <input
              autoFocus
              required
              id="plate_number"
              type="text"
              placeholder="e.g. UBA 123X"
              autoComplete="off"
              value={plateNumber}
              onChange={handleChange}
            />
            <Button
              type="submit"
              variant="contained"
              disableElevation
              disabled={loading || !plateNumber.trim()}
              startIcon={
                loading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <SearchRounded />
                )
              }
              sx={{
                borderRadius: "var(--lp-r-sm)",
                textTransform: "none",
                fontWeight: 600,
                px: 2.5,
                whiteSpace: "nowrap",
              }}
            >
              {loading ? "Checking" : "Check"}
            </Button>
          </div>
        </form>

        {statusError && (
          <Banner className="danger" role="alert">
            <ErrorRounded />
            <div>
              <strong>Could not check this vehicle</strong>
              <span>Check the plate number and try again.</span>
            </div>
          </Banner>
        )}

        {result && (
          <>
            <Banner className={tone(status)}>
              {isClear ? <CheckCircleRounded /> : <ErrorRounded />}
              <div>
                <strong>{isClear ? "No pending invoices" : status}</strong>
                <span>
                  {isClear
                    ? `${plateNumber} is clear to park.`
                    : `${plateNumber} has unpaid parking invoices.`}
                </span>
              </div>
            </Banner>

            {isDefaulted && (
              <>
                <Stats>
                  <div className="stat">
                    <span className="label">Times defaulted</span>
                    <span className="value">
                      {result.number_of_times_defaulted ?? 0}
                    </span>
                  </div>
                  <div className="stat">
                    <span className="label">Total outstanding</span>
                    <span className="value danger">
                      {formatAmount(result.total_outstanding ?? 0)}
                    </span>
                  </div>
                  {result.car_type && (
                    <div className="stat">
                      <span className="label">Car type</span>
                      <span className="value">{result.car_type}</span>
                    </div>
                  )}
                </Stats>

                {result.details?.length > 0 && (
                  <Invoices>
                    <h3>Unpaid invoices ({result.details.length})</h3>
                    {result.details.map((detail: any, index: number) => (
                      <div
                        className="invoice"
                        key={detail.invoice_number ?? index}
                      >
                        <div className="invoice-head">
                          <span className="number">
                            <ReceiptLongOutlined fontSize="inherit" />
                            {detail.invoice_number}
                          </span>
                          <span className="amount">
                            {formatAmount(detail.outstanding_balance ?? 0)}
                          </span>
                        </div>
                        <dl>
                          <div>
                            <dt>City</dt>
                            <dd>{detail.district || "-"}</dd>
                          </div>
                          <div>
                            <dt>Parking yard</dt>
                            <dd>{detail.parking_yard || "-"}</dd>
                          </div>
                          <div>
                            <dt>Days past due</dt>
                            <dd>{detail.days_past_due ?? "-"}</dd>
                          </div>
                          <div>
                            <dt>Date</dt>
                            <dd>
                              {detail.date_time
                                ? new Date(detail.date_time).toLocaleString()
                                : "-"}
                            </dd>
                          </div>
                        </dl>
                      </div>
                    ))}
                  </Invoices>
                )}
              </>
            )}
          </>
        )}
      </Body>

      <Footer>
        <Button
          onClick={handleClose}
          sx={{ textTransform: "none", color: "var(--lp-muted)" }}
        >
          Close
        </Button>
        {isDefaulted && (
          <div className="enforce">
            <Button
              variant="outlined"
              onClick={() => goToClamping("notified")}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "var(--lp-r-sm)",
              }}
            >
              Notify
            </Button>
            <Button
              variant="contained"
              color="error"
              disableElevation
              onClick={() => goToClamping("clamped")}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "var(--lp-r-sm)",
              }}
            >
              Clamp
            </Button>
          </div>
        )}
      </Footer>
    </Dialog>
  );
};

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 18px 20px 16px;
  border-bottom: 1px solid var(--lp-border);

  .icon-tile {
    display: grid;
    place-items: center;
    flex-shrink: 0;
    width: 38px;
    height: 38px;
    border-radius: var(--lp-r-sm);
    background: var(--lp-primary-soft);
    color: var(--lp-primary);
  }

  .heading {
    flex: 1;
    min-width: 0;
  }

  h2 {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    color: var(--lp-text);
    letter-spacing: -0.01em;
  }

  p {
    margin: 2px 0 0;
    font-size: 13px;
    color: var(--lp-muted);
  }
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  max-height: 65vh;
  overflow-y: auto;

  .search label {
    display: block;
    margin-bottom: 6px;
    font-size: 12px;
    font-weight: 600;
    color: var(--lp-muted);
  }

  .search-row {
    display: flex;
    gap: 8px;

    @media (max-width: 480px) {
      flex-direction: column;
    }
  }

  .search input {
    flex: 1;
    min-width: 0;
    height: 44px;
    padding: 0 14px;
    border: 1px solid var(--lp-border);
    border-radius: var(--lp-r-sm);
    background: var(--lp-bg-alt);
    font-size: 15px;
    font-weight: 600;
    letter-spacing: 0.04em;
    color: var(--lp-text);
    outline: none;
    transition:
      border-color 0.15s ease,
      box-shadow 0.15s ease,
      background 0.15s ease;

    &::placeholder {
      font-weight: 400;
      letter-spacing: 0;
      color: var(--lp-muted);
      opacity: 0.7;
    }

    &:focus {
      background: var(--lp-raised);
      border-color: var(--lp-primary);
      box-shadow: 0 0 0 3px var(--lp-primary-soft);
    }
  }
`;

const Banner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border-radius: var(--lp-r-sm);
  border: 1px solid;

  svg {
    flex-shrink: 0;
    font-size: 22px;
  }

  div {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  strong {
    font-size: 14px;
    font-weight: 700;
  }

  span {
    font-size: 13px;
    color: var(--lp-muted);
  }

  &.clear {
    background: #edf7ee;
    border-color: #c8e6c9;
    color: #2e7d32;
  }

  &.danger {
    background: #fdeeee;
    border-color: #f5c6c6;
    color: #c62828;
  }

  &.warn {
    background: var(--lp-accent-soft);
    border-color: #f1dea6;
    color: var(--lp-accent-ink);
  }
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 10px;

  .stat {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 12px 14px;
    border-radius: var(--lp-r-sm);
    background: var(--lp-tile);
  }

  .label {
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--lp-muted);
  }

  .value {
    font-size: 16px;
    font-weight: 700;
    color: var(--lp-text);
    overflow-wrap: anywhere;
  }

  .value.danger {
    color: #c62828;
  }
`;

const Invoices = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  h3 {
    margin: 4px 0 0;
    font-size: 13px;
    font-weight: 700;
    color: var(--lp-text);
  }

  .invoice {
    border: 1px solid var(--lp-border);
    border-radius: var(--lp-r-sm);
    overflow: hidden;
  }

  .invoice-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 9px 14px;
    background: var(--lp-bg-alt);
    border-bottom: 1px solid var(--lp-border);
  }

  .number {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    color: var(--lp-text);
    overflow-wrap: anywhere;
  }

  .amount {
    font-size: 13px;
    font-weight: 700;
    color: #c62828;
    white-space: nowrap;
  }

  dl {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 10px 16px;
    margin: 0;
    padding: 12px 14px;

    @media (max-width: 480px) {
      grid-template-columns: 1fr;
    }
  }

  dt {
    font-size: 11px;
    font-weight: 500;
    color: var(--lp-muted);
  }

  dd {
    margin: 1px 0 0;
    font-size: 13px;
    color: var(--lp-text);
  }
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 20px;
  border-top: 1px solid var(--lp-border);
  background: var(--lp-bg-alt);

  .enforce {
    display: flex;
    gap: 8px;
  }
`;
